"use client";

import { useState } from "react";
import Link from "next/link";
import { CheckCircle2, Loader2 } from "lucide-react";
import type { Locale } from "@/i18n/config";
import type { Dictionary } from "@/i18n/types";
import { WAITLIST_ENDPOINT, validateWaitlist, type WaitlistState } from "@/lib/waitlist";
import { cn } from "@/lib/cn";
import { buttonStyles } from "./ui";

export type FormText = Dictionary["form"] & {
  cta: string;
  /** Mensagem de indisponibilidade já com o e-mail preenchido. */
  unavailable: string;
};

const inputClass =
  "mt-2 block min-h-14 w-full rounded-xl border-2 border-brand-200 bg-white px-4 text-lg text-ink placeholder:text-ink-soft/60 focus:border-brand-600 focus:outline-none aria-[invalid=true]:border-score-low";

function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <p id={id} className="mt-2 text-base font-medium text-[#9a3f28]">
      {message}
    </p>
  );
}

export function WaitlistForm({
  t,
  locale,
  privacyHref,
  idPrefix = "form",
}: {
  t: FormText;
  locale: Locale;
  privacyHref: string;
  /** Prefixo dos ids dos campos: a página pode ter mais de um formulário (popup). */
  idPrefix?: string;
}) {
  const id = (name: string) => `${idPrefix}-${name}`;
  const [state, setState] = useState<WaitlistState>({ status: "idle" });
  const [pending, setPending] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const data = {
      name: String(formData.get("name") ?? "").trim(),
      contact: String(formData.get("contact") ?? "").trim(),
      audience: String(formData.get("audience") ?? ""),
      consent: formData.get("consent") === "on",
      website: String(formData.get("website") ?? ""), // armadilha para robôs
      lang: locale,
    };

    const fields = validateWaitlist(data, t.errors);
    if (Object.keys(fields).length > 0) {
      setState({ status: "error", message: t.errors.check, fields });
      return;
    }

    setPending(true);
    try {
      const response = await fetch(WAITLIST_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = (await response.json().catch(() => null)) as { ok?: boolean } | null;
      if (!response.ok || !result?.ok) throw new Error(`HTTP ${response.status}`);
      setState({ status: "success", name: data.name.split(" ")[0] });
    } catch {
      setState({ status: "error", message: t.unavailable });
    } finally {
      setPending(false);
    }
  }

  if (state.status === "success") {
    return (
      <div role="status" className="rounded-2xl bg-white p-8 text-center text-ink shadow-lift">
        <CheckCircle2 aria-hidden className="mx-auto size-14 text-score-high" />
        <p className="mt-4 text-2xl font-semibold text-brand-950">
          {t.successTitle.replace("{name}", state.name)}
        </p>
        <p className="mt-2 text-lg text-ink-soft">{t.successText}</p>
      </div>
    );
  }

  const errors = state.status === "error" ? state.fields : undefined;

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-2xl bg-white p-6 text-ink shadow-lift sm:p-8"
    >
      <div className="space-y-6">
        {/* Armadilha para robôs: invisível para pessoas, robôs costumam preencher. */}
        <div aria-hidden className="absolute -left-[9999px]">
          <label>
            {t.honeypot}
            <input type="text" name="website" tabIndex={-1} autoComplete="off" />
          </label>
        </div>
        <div>
          <label htmlFor={id("name")} className="text-lg font-semibold text-brand-950">
            {t.name}
          </label>
          <input
            id={id("name")}
            name="name"
            type="text"
            autoComplete="name"
            required
            aria-invalid={Boolean(errors?.name)}
            aria-describedby={errors?.name ? id("name-error") : undefined}
            className={inputClass}
          />
          <FieldError id={id("name-error")} message={errors?.name} />
        </div>

        <div>
          <label htmlFor={id("contact")} className="text-lg font-semibold text-brand-950">
            {t.contact}
          </label>
          <input
            id={id("contact")}
            name="contact"
            type="text"
            inputMode="email"
            autoComplete="email"
            placeholder={t.contactPlaceholder}
            required
            aria-invalid={Boolean(errors?.contact)}
            aria-describedby={errors?.contact ? id("contact-error") : undefined}
            className={inputClass}
          />
          <FieldError id={id("contact-error")} message={errors?.contact} />
        </div>

        <fieldset>
          <legend className="text-lg font-semibold text-brand-950">
            {t.audience} <span className="font-normal text-ink-soft">{t.optional}</span>
          </legend>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {[
              { value: "para-mim", label: t.forMe },
              { value: "para-familia", label: t.forFamily },
            ].map((opt) => (
              <label
                key={opt.value}
                className="flex min-h-14 cursor-pointer items-center gap-3 rounded-xl border-2 border-brand-200 px-4 text-lg has-[:checked]:border-brand-600 has-[:checked]:bg-brand-50"
              >
                <input
                  type="radio"
                  name="audience"
                  value={opt.value}
                  className="size-5 accent-brand-950"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label className="flex cursor-pointer items-start gap-3 text-base text-ink-soft">
            <input
              type="checkbox"
              name="consent"
              required
              aria-invalid={Boolean(errors?.consent)}
              aria-describedby={errors?.consent ? id("consent-error") : undefined}
              className="mt-1 size-5 shrink-0 accent-brand-950"
            />
            <span>
              {t.consent}{" "}
              <Link href={privacyHref} className="font-semibold text-brand-950 underline">
                {t.privacyLink}
              </Link>
              .
            </span>
          </label>
          <FieldError id={id("consent-error")} message={errors?.consent} />
        </div>

        {state.status === "error" && (
          <p role="alert" className="text-base font-medium text-[#9a3f28]">
            {state.message}
          </p>
        )}

        <button
          type="submit"
          disabled={pending}
          className={cn(buttonStyles.primary, "w-full disabled:cursor-wait disabled:opacity-80")}
        >
          {pending && <Loader2 aria-hidden className="size-5 animate-spin" />}
          {pending ? t.sending : t.cta}
        </button>
      </div>
    </form>
  );
}
